// Entscheidungen der Dialoge "Einstellungen" (SettingsChecklist.vue) und "Nach
// Inspect übertragen" (TransferInspectModal.vue). Die Antwortformen legt
// `cld-backend` fest: `visibility_sync` in `checklist-update-settings`, `code` in
// `couchdb-getchecklist` und `couchdb-transferchecklist`.

function isMissingId(id) {
  return id === null || id === undefined || id === "";
}

/**
 * Auswahl und Liste für das Feld "Sichtbarkeit".
 *
 * Eine gespeicherte ID ohne Firma in `companies` bleibt als eigener Eintrag
 * erhalten, auch ein leerer Wert: Speichern schreibt genau die Auswahl, ohne den
 * Eintrag fiele die Einschränkung in Inspect weg. Firmen ohne `company_id` stehen
 * nicht zur Wahl, ihre Auswahl ergäbe im Backend einen leeren Listeneintrag.
 *
 * @param {Array} companyIds `entry.company_list`.
 * @param {Array} companies `company_list` der Antwort, `{company_id, name}`.
 * @param {function} nameFor Anzeigetext für eine ID ohne Firmennamen.
 * @returns {{items: Array, selected: Array}} `selected` ist Teil von `items`.
 */
export function companySelection(companyIds, companies, nameFor) {
  const items = (companies || [])
    .filter((company) => !isMissingId(company.company_id))
    .map((company) => ({
      company_id: company.company_id,
      name: company.name || nameFor(company.company_id),
    }));

  const selected = [];
  (companyIds || []).forEach((id) => {
    let item = isMissingId(id)
      ? items.find((entry) => isMissingId(entry.company_id))
      : items.find((entry) => String(entry.company_id) === String(id));
    if (!item) {
      item = { company_id: id, name: nameFor(id) };
      items.push(item);
    }
    if (selected.indexOf(item) < 0) {
      selected.push(item);
    }
  });

  return { items, selected };
}

/**
 * Rückmeldung nach dem Speichern der Einstellungen.
 *
 * Ohne `visibility_sync` (Backend ohne Abgleich) und bei einem unbekannten Status
 * bleibt es bei der bisherigen Meldung. `checklist_not_found`: es wurde nichts
 * geschrieben. `not_importable`: erneutes Speichern ändert nichts daran.
 *
 * @param {{status?: string, stage?: string}} [sync] `data.visibility_sync`.
 * @returns {{type: string, key: string, retry: boolean}} `type` ist `success`,
 *   `warning` oder `error`, `key` der i18n-Schlüssel. `retry`: der Text bittet um erneutes
 *   Speichern, das Formular bleibt also stehen.
 */
export function visibilitySyncFeedback(sync) {
  const status = sync ? sync.status : undefined;
  if (status === "synced") {
    return { type: "success", key: "CHECKLIST_SETTINGS.SAVED_SYNCED", retry: false };
  }
  if (status === "not_transferred") {
    return { type: "success", key: "CHECKLIST_SETTINGS.SAVED_NOT_TRANSFERRED", retry: false };
  }
  if (status === "kept_restriction") {
    return { type: "warning", key: "CHECKLIST_SETTINGS.SAVED_KEPT_RESTRICTION", retry: false };
  }
  if (status === "checklist_not_found") {
    return { type: "error", key: "CHECKLIST_SETTINGS.NOT_FOUND", retry: false };
  }
  if (status === "error" && sync.code === "not_importable") {
    return { type: "warning", key: "CHECKLIST_SETTINGS.SAVED_NOT_IMPORTABLE", retry: false };
  }
  if (status === "error") {
    return {
      type: "warning",
      key: sync.stage === "import"
        ? "CHECKLIST_SETTINGS.SAVED_ERROR_IMPORT"
        : "CHECKLIST_SETTINGS.SAVED_ERROR_COUCH",
      retry: true,
    };
  }
  return { type: "success", key: "CHECKLISTS.SUCCESS_SAVED", retry: false };
}

/**
 * Ist `e` eine HTTP-Antwort ab 500? `api_error` zeigte dafür nur den Text von
 * axios. `message` ist gesetzt, wenn der Server in der Form des Vertrags antwortet.
 *
 * @returns {{message: ?string}|null}
 */
export function serverFailure(e) {
  const response = e && e.response;
  if (!response || !(response.status >= 500)) {
    return null;
  }
  const data = response.data || {};
  const hasMessage = data.status === "error" && typeof data.message === "string";
  return { message: hasMessage ? data.message : null };
}

// Nur diese Codes gehören in den Dialog. Eine abgelaufene Anmeldung kommt ebenfalls
// als HTTP 200 mit `status: "error"`, aber ohne `code`, und muss zu `api_error`.
const TRANSFER_ERROR_CODES = [
  "conflict",
  "couch_error",
  "couch_timeout",
  "history_error",
  "internal_error",
];

/**
 * Fehlerantwort von `couchdb-getchecklist` und `couchdb-transferchecklist`.
 * `checklistPostReq` weist den Antwortkörper bei `status: "error"` ab, er kommt
 * also als `e` an.
 *
 * @returns {{message: ?string, written: boolean}|null} `null`: kein Fehler dieser
 *   Endpunkte. `written`: das Dokument ist geschrieben, nur der Verlauf fehlt.
 */
export function transferFailure(e) {
  if (!e || e.status !== "error" || TRANSFER_ERROR_CODES.indexOf(e.code) < 0) {
    return null;
  }
  return {
    message: typeof e.message === "string" && e.message ? e.message : null,
    written: e.code === "history_error",
  };
}
